import Script from 'next/script'
import React from 'react'

/*
 * GTM Gateway — two-path design
 *
 * When GTM_BASE_URL / NEXT_PUBLIC_GTM_BASE_URL is set by the build pipeline,
 * the JS loader is served from our own domain via the Google Tag Gateway
 * (first-party path: /metrics). Survives ITP/ad-blockers; Omnibug, Tag
 * Assistant, and GA4 DebugView all work normally because the dataLayer
 * contract and GTM container ID are unchanged.
 *
 * The <noscript> iframe intentionally stays on www.googletagmanager.com —
 * Google's gateway does not support the noscript snippet.
 *
 * Kill switch: unset GTM_BASE_URL for the affected site+env and rebuild.
 * No code deploy required; the loader reverts to www.googletagmanager.com.
 */

const DEFAULT_GTM_BASE_URL = 'https://www.googletagmanager.com'

// /metrics is the first-party gateway path — shared across all sites and environments.
// The trailing slash is required: the gateway routing rule matches `/metrics/*`,
// and Google's setup guide documents the loader src as `/metrics/`.
const GTM_GATEWAY_SCRIPT_PATH = '/metrics/'

const GTM_STANDARD_SCRIPT_PATH = '/gtm.js'

export function getGtmBaseUrl(enableGateway = false) {
  if (!enableGateway) return DEFAULT_GTM_BASE_URL
  const baseUrl = process.env.GTM_BASE_URL || process.env.NEXT_PUBLIC_GTM_BASE_URL
  return baseUrl ? baseUrl.replace(/\/$/, '') : DEFAULT_GTM_BASE_URL
}

/*
 * DO NOT CHANGE THIS CODE WITHOUT GOOD REASON!
 *
 * The functions exported here are responsible for rendering out the
 * Google Tag Manager tags as defined in the following page:
 * https://developers.google.com/tag-manager/quickstart
 *
 * The container ID is supplied by the build process and is specific
 * to each environment.
 *
 * Updated to allow setting initial data layer values from static props
 */

export function renderGtmTags({
  enableGateway = false,
  ...initialDataLayer
}: Record<string, unknown> & { enableGateway?: boolean } = {}) {
  const gtmId = getGtmId()
  const gtmBaseUrl = getGtmBaseUrl(enableGateway)
  // Use the gateway path only when a first-party gateway domain is configured.
  // Fall back to the standard /gtm.js path when loading from www.googletagmanager.com,
  // because /metrics does not exist on Google's CDN domain.
  const isStandardPath = gtmBaseUrl === DEFAULT_GTM_BASE_URL
  const gtmScriptPath = isStandardPath ? GTM_STANDARD_SCRIPT_PATH : GTM_GATEWAY_SCRIPT_PATH
  // The gateway derives the container id from the first-party domain config, so ?id is only appended for /gtm.js.
  const gtmScriptSuffix = isStandardPath ? "'?id='+i+dl" : "''"
  return (
    gtmId && (
      <Script
        id="gtm"
        dangerouslySetInnerHTML={{
          __html: `
          // Define dataLayer and the gtag function
              window.dataLayer = window.dataLayer || [];
              // Push initial data layer values
              window.dataLayer.push(${JSON.stringify(initialDataLayer)});
              function gtag(){dataLayer.push(arguments);}
              // Default ad_storage to 'denied'.
              gtag('consent', 'default', {
                ad_storage: 'denied',
                analytics_storage: 'denied',
                ad_user_data: 'denied',
                ad_personalization: 'denied',
                wait_for_update: 2000, // 2 seconds
              });
              //Default ad_storage to 'denied' for UET Consent mode
              window.uetq = window.uetq || [];
              window.uetq.push('consent', 'default', {
              'ad_storage': 'denied'
              });

              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              '${gtmBaseUrl}${gtmScriptPath}'+(${gtmScriptSuffix});f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${gtmId}');`,
        }}
      />
    )
  )
}

export function renderGtmNoScriptTag() {
  const gtmId = getGtmId()
  // Per Google's gateway setup guide: "Google tag gateway for advertisers does not support
  // the <noscript> snippet. To use <noscript> functionality, continue using the standard
  // Google Tag Manager <noscript> snippet pointing to www.googletagmanager.com."
  // We therefore always use DEFAULT_GTM_BASE_URL here, even when the gateway is active.
  return (
    !!gtmId && (
      <noscript
        dangerouslySetInnerHTML={{
          __html: `<iframe
          src="${DEFAULT_GTM_BASE_URL}/ns.html?id=${gtmId}"
          height="0"
          width="0"
          style="display:none;visibility:hidden"
          ></iframe>`,
        }}
      />
    )
  )
}

/**
 * Get the Google Tag Manager ID. This should always be available in the
 * sever-side code as `process.env.GTM_ID`, but may also be required when
 * events are fired from the client-side code, where it can be found in
 * `process.env.NEXT_PUBLIC_GTM_ID`
 */
export function getGtmId() {
  return process.env.GTM_ID || process.env.NEXT_PUBLIC_GTM_ID || ''
}
